import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, Reorder } from 'framer-motion';
import { projectsAPI, templatesAPI, getMediaUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft,
  Upload,
  Video,
  Music,
  Type,
  Palette,
  Wand2,
  Play,
  Pause,
  Loader2,
  CheckCircle,
  X,
  FileVideo,
  FileAudio,
  GripVertical,
  Trash2,
  Plus,
  Scissors,
  Settings,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const TRANSITIONS = [
  { id: 'cut', name: 'Cut', desc: 'Direct cut (no transition)' },
  { id: 'fade', name: 'Fade', desc: 'Smooth fade to black' },
  { id: 'dissolve', name: 'Dissolve', desc: 'Cross dissolve blend' },
  { id: 'wipe', name: 'Wipe', desc: 'Directional wipe' },
  { id: 'zoom', name: 'Zoom', desc: 'Zoom transition' },
];

const Editor = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const isNew = projectId === 'new';
  const fileInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [title, setTitle] = useState('Untitled Project');
  const [videoType, setVideoType] = useState('vlog');
  const [captionStyle, setCaptionStyle] = useState('modern');
  const [musicPreference, setMusicPreference] = useState('auto');
  
  // Clips state
  const [clips, setClips] = useState([]);
  const [uploadingClips, setUploadingClips] = useState([]);
  
  // Templates
  const [videoTypes, setVideoTypes] = useState([]);
  const [captionStyles, setCaptionStyles] = useState([]);

  // Processing status
  const [processingStatus, setProcessingStatus] = useState(null);

  // Clip editor dialog
  const [editingClip, setEditingClip] = useState(null);
  const [clipTrimStart, setClipTrimStart] = useState(0);
  const [clipTrimEnd, setClipTrimEnd] = useState(100);
  const [clipTransition, setClipTransition] = useState('cut');
  const clipVideoRef = useRef(null);

  // Project settings
  const [transitionDuration, setTransitionDuration] = useState(0.5);
  const [outputResolution, setOutputResolution] = useState('1080p');

  useEffect(() => {
    fetchTemplates();
    if (!isNew) {
      fetchProject();
    }
  }, [projectId, isNew]);

  // Poll processing status
  useEffect(() => {
    let interval;
    if (project?.status && ['processing', 'analyzing', 'cutting', 'audio_sync', 'captions', 'color_grading', 'effects', 'encoding'].includes(project.status)) {
      setProcessing(true);
      interval = setInterval(async () => {
        try {
          const response = await projectsAPI.getStatus(project.project_id);
          setProcessingStatus(response.data);
          
          if (response.data.status === 'completed' || response.data.status === 'error') {
            clearInterval(interval);
            setProcessing(false);
            await fetchProject();
            if (response.data.status === 'completed') {
              toast.success('Video processing completed!');
            } else {
              toast.error('Processing failed: ' + response.data.error_message);
            }
          }
        } catch (error) {
          console.error('Status poll error:', error);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [project?.status, project?.project_id]);

  const fetchTemplates = async () => {
    try {
      const [typesRes, stylesRes] = await Promise.all([
        templatesAPI.getVideoTypes(),
        templatesAPI.getCaptionStyles()
      ]);
      setVideoTypes(typesRes.data);
      setCaptionStyles(stylesRes.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.get(projectId);
      const proj = response.data;
      setProject(proj);
      setTitle(proj.title);
      setVideoType(proj.video_type);
      setCaptionStyle(proj.caption_style);
      setMusicPreference(proj.music_preference);
      setClips(proj.clips || []);
      if (proj.settings) {
        setTransitionDuration(proj.settings.transition_duration || 0.5);
        setOutputResolution(proj.settings.output_resolution || '1080p');
      }
    } catch (error) {
      toast.error('Project not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    setSaving(true);
    try {
      const response = await projectsAPI.create({
        title,
        video_type: videoType,
        caption_style: captionStyle,
        music_preference: musicPreference
      });
      setProject(response.data);
      setClips([]);
      navigate(`/editor/${response.data.project_id}`, { replace: true });
      toast.success('Project created');
      return response.data;
    } catch (error) {
      toast.error('Failed to create project');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProject = async () => {
    if (!project) return;
    setSaving(true);
    try {
      await projectsAPI.update(project.project_id, {
        title,
        video_type: videoType,
        caption_style: captionStyle,
        music_preference: musicPreference
      });
      // Also update settings
      await projectsAPI.updateSettings(project.project_id, {
        transition_duration: transitionDuration,
        output_resolution: outputResolution,
        output_format: 'mp4'
      });
      toast.success('Project saved');
    } catch (error) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    let currentProject = project;
    
    if (!currentProject) {
      currentProject = await handleCreateProject();
      if (!currentProject) return;
    }

    for (const file of files) {
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      setUploadingClips(prev => [...prev, { id: tempId, name: file.name, progress: 0 }]);

      try {
        const response = await projectsAPI.uploadClip(
          currentProject.project_id, 
          file, 
          (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadingClips(prev => prev.map(c => c.id === tempId ? {...c, progress} : c));
          }
        );
        
        setClips(prev => [...prev, response.data.clip]);
        setUploadingClips(prev => prev.filter(c => c.id !== tempId));
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        setUploadingClips(prev => prev.filter(c => c.id !== tempId));
        toast.error(error.response?.status === 413 ? `${file.name}: File too large` : `Failed to upload ${file.name}`);
      }
    }
    
    await fetchProject();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteClip = async (clipId) => {
    if (!project) return;
    try {
      await projectsAPI.deleteClip(project.project_id, clipId);
      setClips(prev => prev.filter(c => c.clip_id !== clipId));
      toast.success('Clip removed');
    } catch (error) {
      toast.error('Failed to remove clip');
    }
  };

  const handleReorderClips = async (newOrder) => {
    setClips(newOrder);
    if (!project) return;
    try {
      await projectsAPI.reorderClips(project.project_id, newOrder.map(c => c.clip_id));
    } catch (error) {
      console.error('Failed to save clip order:', error);
    }
  };

  const handleEditClip = (clip) => {
    setEditingClip(clip);
    setClipTrimStart(clip.trim_start || 0);
    setClipTrimEnd(clip.trim_end || 100);
    setClipTransition(clip.transition || 'cut');
  };

  const handleSaveClipEdit = async () => {
    if (!project || !editingClip) return;
    try {
      await projectsAPI.updateClip(project.project_id, editingClip.clip_id, {
        trim_start: clipTrimStart,
        trim_end: clipTrimEnd,
        transition: clipTransition
      });
      
      setClips(prev => prev.map(c => 
        c.clip_id === editingClip.clip_id 
          ? { ...c, trim_start: clipTrimStart, trim_end: clipTrimEnd, transition: clipTransition }
          : c
      ));
      
      setEditingClip(null);
      toast.success('Clip settings saved');
    } catch (error) {
      toast.error('Failed to save clip settings');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    let currentProject = project;
    if (!currentProject) {
      currentProject = await handleCreateProject();
      if (!currentProject) return;
    }

    try {
      await projectsAPI.uploadAudio(currentProject.project_id, file);
      toast.success('Audio uploaded');
      await fetchProject();
    } catch (error) {
      toast.error('Failed to upload audio');
    }
  };

  const handleStartProcessing = async () => {
    if (!project || clips.length === 0) {
      toast.error('Please upload at least one video clip');
      return;
    }
    
    try {
      await projectsAPI.startProcessing(project.project_id);
      setProcessing(true);
      setProcessingStatus({ status: 'processing', progress: 0 });
      toast.success('Processing started');
      await fetchProject();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Free tier limit reached. Please upgrade to Pro.');
        navigate('/subscription');
      } else {
        toast.error('Failed to start processing');
      }
    }
  };

  const getProgressLabel = () => {
    const labels = {
      'processing': 'Starting...',
      'analyzing': 'Analyzing clips...',
      'cutting': 'Trimming & arranging...',
      'audio_sync': 'Syncing audio...',
      'captions': 'Generating captions...',
      'color_grading': 'Color grading...',
      'effects': 'Adding transitions & effects...',
      'encoding': 'Encoding final video...'
    };
    return labels[processingStatus?.status] || 'Processing...';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#CCFF00] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303]">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="text-white/60 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-transparent border-none text-white font-heading font-semibold text-xl focus:ring-0 w-auto" data-testid="project-title-input" />
          </div>
          <div className="flex items-center gap-4">
            <Button variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10" onClick={isNew ? handleCreateProject : handleUpdateProject} disabled={saving} data-testid="save-project-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isNew ? 'Create' : 'Save'}
            </Button>
            {clips.length > 0 && !processing && project?.status !== 'completed' && (
              <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600]" onClick={handleStartProcessing} data-testid="start-processing-btn">
                <Wand2 className="w-4 h-4 mr-2" />
                Process {clips.length} Clip{clips.length !== 1 ? 's' : ''}
              </Button>
            )}
            {project?.status === 'completed' && (
              <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600]" onClick={() => navigate(`/preview/${project.project_id}`)} data-testid="preview-btn">
                <Play className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20 pb-8 px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Clips */}
            <div className="lg:col-span-2 space-y-6">
              {/* Processing Status */}
              {processing && (
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
                  <Card className="bg-[#0A0A0A] border-[#CCFF00]/30 p-6 rounded-2xl processing-glow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-[#CCFF00]/20 flex items-center justify-center">
                        <Wand2 className="w-5 h-5 text-[#CCFF00] animate-pulse" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-white">{getProgressLabel()}</h3>
                        <p className="text-white/60 text-sm">AI is editing your {clips.length} clips</p>
                      </div>
                    </div>
                    <Progress value={processingStatus?.progress || 0} className="h-2 bg-white/10" />
                    <p className="text-right text-white/40 text-sm mt-2">{processingStatus?.progress || 0}%</p>
                  </Card>
                </motion.div>
              )}

              {/* Video Clips */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#CCFF00]/10 flex items-center justify-center">
                      <Video className="w-5 h-5 text-[#CCFF00]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-white">Video Clips</h3>
                      <p className="text-white/60 text-sm">Upload, trim & arrange your footage</p>
                    </div>
                  </div>
                  <span className="text-white/40 text-sm">{clips.length} clip{clips.length !== 1 ? 's' : ''}</span>
                </div>

                {/* Upload Area */}
                <label className="block cursor-pointer mb-6">
                  <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-[#CCFF00]/50 transition-colors">
                    <Upload className="w-10 h-10 text-white/20 mx-auto mb-3" />
                    <p className="text-white mb-1">Drop video files or click to browse</p>
                    <p className="text-white/40 text-sm">Select multiple • Free: 2GB / Pro: 10GB per file</p>
                  </div>
                  <input ref={fileInputRef} type="file" accept="video/*" multiple className="hidden" onChange={handleFileSelect} data-testid="video-upload-input" />
                </label>

                {/* Uploading */}
                {uploadingClips.length > 0 && (
                  <div className="space-y-3 mb-6">
                    {uploadingClips.map((upload) => (
                      <div key={upload.id} className="bg-[#121212] rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <Loader2 className="w-5 h-5 text-[#CCFF00] animate-spin" />
                          <span className="text-white text-sm flex-1 truncate">{upload.name}</span>
                          <span className="text-white/60 text-sm">{upload.progress}%</span>
                        </div>
                        <Progress value={upload.progress} className="h-1 bg-white/10" />
                      </div>
                    ))}
                  </div>
                )}

                {/* Clips List */}
                {clips.length > 0 ? (
                  <div className="space-y-3">
                    <p className="text-sm text-white/40 px-2">Drag to reorder • Click trim icon to edit</p>
                    <Reorder.Group axis="y" values={clips} onReorder={handleReorderClips} className="space-y-2">
                      {clips.map((clip, index) => (
                        <Reorder.Item key={clip.clip_id} value={clip} className="bg-[#121212] rounded-xl p-4 cursor-grab active:cursor-grabbing">
                          <div className="flex items-center gap-4">
                            <GripVertical className="w-5 h-5 text-white/30" />
                            <div className="w-8 h-8 rounded-lg bg-[#CCFF00]/20 flex items-center justify-center text-[#CCFF00] font-semibold text-sm">
                              {index + 1}
                            </div>
                            
                            {/* Thumbnail */}
                            <div className="w-24 h-14 bg-black rounded-lg overflow-hidden relative group">
                              <video src={getMediaUrl(clip.url)} className="w-full h-full object-cover" preload="metadata" muted
                                onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium truncate">{clip.filename}</p>
                              <div className="flex items-center gap-3 text-white/40 text-sm">
                                <span>{formatFileSize(clip.size)}</span>
                                {clip.trim_start > 0 || clip.trim_end < 100 ? (
                                  <span className="text-[#CCFF00]">Trimmed</span>
                                ) : null}
                                <span className="capitalize">{clip.transition || 'cut'}</span>
                              </div>
                            </div>
                            
                            <Button variant="ghost" size="icon" className="text-white/40 hover:text-[#CCFF00]" onClick={() => handleEditClip(clip)} title="Trim & Edit">
                              <Scissors className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="text-white/40 hover:text-red-500" onClick={() => handleDeleteClip(clip.clip_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Transition indicator */}
                          {index < clips.length - 1 && (
                            <div className="flex items-center justify-center mt-3 pt-3 border-t border-white/5">
                              <div className="flex items-center gap-2 text-white/30 text-xs">
                                <Sparkles className="w-3 h-3" />
                                <span className="capitalize">{clip.transition || 'cut'} transition</span>
                              </div>
                            </div>
                          )}
                        </Reorder.Item>
                      ))}
                    </Reorder.Group>
                    
                    <label className="block cursor-pointer">
                      <div className="border border-dashed border-white/10 rounded-xl p-4 text-center hover:border-[#CCFF00]/50 transition-colors flex items-center justify-center gap-2">
                        <Plus className="w-5 h-5 text-white/40" />
                        <span className="text-white/60">Add more clips</span>
                      </div>
                      <input type="file" accept="video/*" multiple className="hidden" onChange={handleFileSelect} />
                    </label>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Video className="w-12 h-12 text-white/10 mx-auto mb-3" />
                    <p className="text-white/40">No clips uploaded yet</p>
                  </div>
                )}
              </Card>

              {/* Audio */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-[#6E44FF]/10 flex items-center justify-center">
                    <Music className="w-5 h-5 text-[#6E44FF]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white">External Audio</h3>
                    <p className="text-white/60 text-sm">Optional background audio track</p>
                  </div>
                </div>

                {project?.audio_url ? (
                  <div className="bg-[#121212] rounded-xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileAudio className="w-8 h-8 text-[#6E44FF]" />
                      <div>
                        <p className="text-white font-medium">Audio uploaded</p>
                        <p className="text-white/40 text-sm">Ready</p>
                      </div>
                    </div>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center hover:border-[#6E44FF]/50 transition-colors">
                      <Music className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <p className="text-white/60 text-sm">Drop audio or click to browse</p>
                    </div>
                    <input type="file" accept="audio/*" className="hidden" onChange={handleAudioUpload} data-testid="audio-upload-input" />
                  </label>
                )}
              </Card>
            </div>

            {/* Right Column - Settings */}
            <div className="space-y-6">
              {/* Video Style */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Palette className="w-5 h-5 text-[#CCFF00]" />
                  <Label className="text-white font-medium">Video Style</Label>
                </div>
                <Select value={videoType} onValueChange={setVideoType}>
                  <SelectTrigger className="bg-[#121212] border-white/10 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {videoTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id} className="text-white">
                        <div><p>{type.name}</p><p className="text-white/40 text-xs">{type.description}</p></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Card>

              {/* Caption Style */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Type className="w-5 h-5 text-[#CCFF00]" />
                  <Label className="text-white font-medium">Caption Style</Label>
                </div>
                <Select value={captionStyle} onValueChange={setCaptionStyle}>
                  <SelectTrigger className="bg-[#121212] border-white/10 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {captionStyles.map((style) => (
                      <SelectItem key={style.id} value={style.id} className="text-white">
                        <div><p>{style.name}</p><p className="text-white/40 text-xs">{style.preview}</p></div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Card>

              {/* Output Settings */}
              <Card className="bg-[#0A0A0A] border-white/10 p-6 rounded-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-5 h-5 text-[#CCFF00]" />
                  <Label className="text-white font-medium">Output Settings</Label>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-white/60 text-sm mb-2 block">Resolution</Label>
                    <Select value={outputResolution} onValueChange={setOutputResolution}>
                      <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="720p" className="text-white">720p HD</SelectItem>
                        <SelectItem value="1080p" className="text-white">1080p Full HD</SelectItem>
                        <SelectItem value="4k" className="text-white">4K Ultra HD (Pro)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-white/60 text-sm mb-2 block">Transition Duration: {transitionDuration}s</Label>
                    <Slider value={[transitionDuration]} onValueChange={([v]) => setTransitionDuration(v)} min={0.1} max={2} step={0.1} className="py-2" />
                  </div>
                </div>
              </Card>

              {/* Summary */}
              <Card className="bg-gradient-to-br from-[#CCFF00]/10 to-[#6E44FF]/10 border-white/10 p-6 rounded-2xl">
                <h3 className="font-heading font-semibold text-white mb-3">Summary</h3>
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between"><span className="text-white/60">Clips</span><span className="text-white">{clips.length}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Style</span><span className="text-white capitalize">{videoType.replace('_', ' ')}</span></div>
                  <div className="flex justify-between"><span className="text-white/60">Resolution</span><span className="text-white">{outputResolution}</span></div>
                </div>
                
                {isNew ? (
                  <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-xl" onClick={handleCreateProject} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Project
                  </Button>
                ) : clips.length > 0 && !processing && project?.status !== 'completed' ? (
                  <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-xl" onClick={handleStartProcessing}>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Start AI Editing
                  </Button>
                ) : project?.status === 'completed' ? (
                  <Button className="w-full bg-[#CCFF00] text-black hover:bg-[#B3E600] rounded-xl" onClick={() => navigate(`/preview/${project.project_id}`)}>
                    <Play className="w-4 h-4 mr-2" />
                    View Result
                  </Button>
                ) : null}
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Clip Edit Dialog */}
      <Dialog open={!!editingClip} onOpenChange={() => setEditingClip(null)}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Scissors className="w-5 h-5 text-[#CCFF00]" />
              Edit Clip: {editingClip?.filename}
            </DialogTitle>
          </DialogHeader>
          
          {editingClip && (
            <div className="space-y-6">
              {/* Video Preview */}
              <div className="aspect-video bg-black rounded-xl overflow-hidden">
                <video ref={clipVideoRef} src={getMediaUrl(editingClip.url)} className="w-full h-full object-contain" controls preload="metadata" />
              </div>

              {/* Trim Controls */}
              <div>
                <Label className="text-white/70 mb-3 block">Trim Points (% of video)</Label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-white/40 text-xs mb-1 block">Start: {clipTrimStart}%</Label>
                    <Slider value={[clipTrimStart]} onValueChange={([v]) => setClipTrimStart(v)} min={0} max={clipTrimEnd - 1} step={1} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-white/40 text-xs mb-1 block">End: {clipTrimEnd}%</Label>
                    <Slider value={[clipTrimEnd]} onValueChange={([v]) => setClipTrimEnd(v)} min={clipTrimStart + 1} max={100} step={1} />
                  </div>
                </div>
              </div>

              {/* Transition */}
              <div>
                <Label className="text-white/70 mb-3 block">Transition to Next Clip</Label>
                <div className="grid grid-cols-5 gap-2">
                  {TRANSITIONS.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setClipTransition(t.id)}
                      className={`p-3 rounded-xl border text-center transition-all ${
                        clipTransition === t.id
                          ? 'bg-[#CCFF00]/20 border-[#CCFF00] text-[#CCFF00]'
                          : 'bg-[#121212] border-white/10 text-white/70 hover:border-white/30'
                      }`}
                    >
                      <p className="text-sm font-medium">{t.name}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 border-white/10 text-white" onClick={() => setEditingClip(null)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600]" onClick={handleSaveClipEdit}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Editor;
